import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, ScanLine, Camera, UploadCloud, FileText, FileImage, RotateCw, AlertTriangle } from "lucide-react";
import ImportReviewDialog from "@/components/ImportReviewDialog";
import { useExtraction } from "@/context/ExtractionContext";

const upper = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([, v]) => String(v || "").trim()).map(([k, v]) => [k, String(v).toUpperCase()])
);

// Zona di trascinamento + pulsanti per la lettura AI dei documenti.
// L'estrazione gira in background sul server (niente timeout) e prosegue anche cambiando pagina.
export function ImportDocsButtons({ onExtract, currentValues = null }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const { jobs, start, claim, watch } = useExtraction();
  const [jobId, setJobId] = useState(null);
  const [drag, setDrag] = useState(false);
  const [review, setReview] = useState(null);
  const [failed, setFailed] = useState([]);
  const acc = useRef({ fields: {}, enriched: [], infoFiles: [] });
  const ultimoDrop = useRef(0);

  const job = jobs.find((j) => j.id === jobId) || null;
  const loading = job?.stato === "in_corso";
  const pronto = jobs.find((j) => j.stato === "completo" && j.id !== jobId);

  useEffect(() => { if (jobId) { watch(jobId, true); return () => watch(jobId, false); } }, [jobId, watch]);

  const finish = useCallback((j) => {
    claim(j.id);
    setJobId(null);
    const res = j.result || {};
    const fields = { ...acc.current.fields, ...upper(res.fields) };
    const nuovi = j.files.map((file, i) => ({ file, tipo: res.files?.[i]?.tipo || "" }));
    const enriched = [...acc.current.enriched.filter((e) => !j.files.includes(e.file)), ...nuovi];
    const infoFiles = [...acc.current.infoFiles.filter((f) => !j.files.includes(f.file)),
      ...(res.files || []).map((f, i) => ({ ...f, file: j.files[i], n: Object.keys(f.campi || {}).length }))];
    acc.current = { fields, enriched, infoFiles };
    const ko = (res.files || []).map((f, i) => ({ ...f, file: j.files[i] })).filter((f) => f.errore);
    setFailed((prev) => [...prev.filter((p) => !j.files.includes(p.file)),
      ...ko.map((f) => ({ name: f.filename || f.file?.name || "documento", file: f.file }))]);
    if (ko.length > 0) toast.warning(`${ko.length} documento/i non letto/i: puoi riprovare solo quello.`);
    if (Object.keys(fields).length === 0) {
      acc.current = { fields: {}, enriched: [], infoFiles: [] };
      onExtract({}, []);  // nessun dato utile: il documento non viene allegato né caricato
      toast.warning("Nessun dato utile riconosciuto: il documento NON è stato allegato. Se vuoi conservarlo, usa la sezione “Documenti” in fondo.", { duration: 8000 });
    } else if (currentValues) {
      setReview({ estratti: fields, enriched, infoFiles });
    } else {
      onExtract(fields, enriched);
      toast.success(`Dati estratti da ${res.analizzati ?? enriched.length} documento/i. Controlla e correggi prima di salvare.`);
    }
  }, [claim, currentValues, onExtract]);

  useEffect(() => {
    if (!job) return;
    if (job.stato === "completo") finish(job);
    else if (job.stato === "errore") { toast.error(job.messaggio || "Estrazione non riuscita"); claim(job.id); setJobId(null); }
  }, [job, finish, claim]);

  const run = async (list, source) => {
    const files = Array.from(list || []);
    if (files.length === 0) return;
    if (files.length > 8) return toast.error("Massimo 8 documenti per volta.");
    const big = files.find((f) => f.size > 25 * 1024 * 1024);
    if (big) return toast.error(`"${big.name}" supera 25 MB (max per documento).`);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const id = await start({ url: "/clienti/estrai-documenti/start", payload: fd, files, label: source });
      setJobId(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Avvio della lettura non riuscito");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const btn = "inline-flex items-center gap-2 text-xs border border-[#C9A84C] text-[#8a6d1f] bg-white px-3 py-2 rounded-sm hover:bg-[#faf5e6] transition-colors disabled:opacity-50";
  const pct = job?.totale ? Math.round(((job.fatti || 0) / job.totale) * 100) : 0;

  return (
    <>
    <div
      onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => { e.preventDefault(); if (!loading) setDrag(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); ultimoDrop.current = Date.now(); setDrag(false); if (!loading) run(e.dataTransfer.files, "file"); }}
      onClick={(e) => {
        // clic proveniente da una finestra sovrapposta (portale) o generato dal rilascio: ignora
        if (!e.currentTarget.contains(e.target)) return;
        if (Date.now() - ultimoDrop.current < 1500) return;
        if (!loading) fileRef.current?.click();
      }}
      data-testid="import-docs-dropzone"
      className={`relative cursor-pointer rounded-sm border-2 border-dashed px-6 py-8 text-center transition-colors ${
        drag ? "border-[#C9A84C] bg-[#f7edcf]" : "border-[#C9A84C]/50 bg-[#fbf7ec]/60 hover:bg-[#faf5e6]"
      }`}>
      {loading ? (
        <div data-testid="import-docs-progress" onClick={(e) => e.stopPropagation()} className="space-y-3">
          <p className="flex items-center justify-center gap-2 text-sm text-[#1A2B4A]">
            <Loader2 className="w-4 h-4 animate-spin text-[#C9A84C]" />
            Lettura in corso… {job.fatti || 0} di {job.totale} documenti
          </p>
          <div className="mx-auto h-1.5 w-full max-w-sm bg-[#1A2B4A]/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#C9A84C] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <ul className="flex flex-wrap justify-center gap-2 text-[11px] text-[#4a5568]">
            {(job.files || []).map((f) => (
              <li key={f.name} className="inline-flex items-center gap-1 bg-white border border-[#1A2B4A]/10 px-2 py-1 rounded-sm">
                {/\.pdf$/i.test(f.name) ? <FileText className="w-3 h-3" /> : <FileImage className="w-3 h-3" />}
                <span className="max-w-[160px] truncate">{f.name}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-[#4a5568]">Puoi anche cambiare pagina: la lettura prosegue sul server e ti avviso quando è pronta.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <UploadCloud className={`w-9 h-9 mx-auto ${drag ? "text-[#C9A84C]" : "text-[#C9A84C]/70"}`} />
          <div>
            <p className="text-sm text-[#1A2B4A] font-medium">Trascina qui i documenti (immagini o PDF)</p>
            <p className="text-[11px] text-[#4a5568] mt-1">Fino a 8 documenti, max 25 MB ciascuno — oppure usa i pulsanti</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button type="button" data-testid="import-docs-btn" onClick={() => fileRef.current?.click()} className={btn}>
              <ScanLine className="w-4 h-4" /> Importa da documenti (AI)
            </button>
            <button type="button" data-testid="import-camera-btn" onClick={() => camRef.current?.click()} className={btn}>
              <Camera className="w-4 h-4" /> Importa da fotocamera
            </button>
          </div>
        </div>
      )}

      {!loading && pronto && (
        <div data-testid="import-docs-ready" onClick={(e) => e.stopPropagation()}
          className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-[#276749] bg-white border border-[#276749]/30 rounded-sm px-3 py-2">
          Lettura completata su {pronto.totale} documento/i mentre eri altrove.
          <button type="button" data-testid="import-docs-apply-ready" onClick={() => finish(pronto)}
            className="inline-flex items-center gap-1.5 bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm hover:bg-[#22345c]">
            Applica i dati letti
          </button>
        </div>
      )}

      {!loading && failed.length > 0 && (
        <div data-testid="import-docs-failed" onClick={(e) => e.stopPropagation()} className="mt-3 space-y-1.5 text-left">
          {failed.map((f) => (
            <div key={f.name} className="flex items-center gap-2 text-xs bg-white border border-[#9b2c2c]/30 rounded-sm px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 text-[#9b2c2c] shrink-0" />
              <span className="flex-1 truncate text-[#9b2c2c]">Non letto: {f.name}</span>
              <button type="button" data-testid={`import-docs-retry-${f.name}`} onClick={() => run([f.file], "file")}
                className="inline-flex items-center gap-1.5 border border-[#1A2B4A] px-2.5 py-1 rounded-sm hover:bg-[#f9f8f5]">
                <RotateCw className="w-3.5 h-3.5" /> Riprova
              </button>
            </div>
          ))}
        </div>
      )}

      <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
        data-testid="import-docs-input" onChange={(e) => run(e.target.files, "file")} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="import-camera-input" onChange={(e) => run(e.target.files, "cam")} />
    </div>

    {review && createPortal(
      <div onClick={(e) => e.stopPropagation()}>
        <ImportReviewDialog estratti={review.estratti} files={review.infoFiles} currentValues={currentValues}
          onApply={(scelti) => {
            const n = Object.keys(scelti || {}).length;
            onExtract(scelti, n > 0 ? review.enriched : []);  // senza campi applicati non si allega nulla
            toast.success(n > 0 ? `${n} campi applicati all'anagrafica.`
              : "Nessun campo applicato: il documento non è stato allegato.");
          }}
          onClose={() => setReview(null)} />
      </div>, document.body)}
    </>
  );
}
